import {
  ORDER_TYPES,
  PAYMENT_METHOD_TYPES,
  PROVIDERS,
} from '@exodus/fiat-client';
import LoadingSpinner from '@passkeys/ui/loading-spinner';
import { useCallback, useEffect, useState } from 'react';

import type { Asset } from '@/types';
import exodus from '@/ui/exodus';
import { actions } from '@/ui/flux';
import useBuyProvider from '@/ui/hooks/use-buy-provider';
import useReceiveAddress from '@/ui/hooks/use-receive-address';
import BlockchainWidget from './components/blockchain-widget.js';
import MoonPayWidget from './components/moonpay-widget.js';

const DEFAULT_PAYMENT_METHOD = 'card';

type FiatBuyWidgetModalProps = {
  orderId: string;
  cryptoAsset: Asset;
  fiatAsset: Asset;
  fiatAmount: number;
  onOrderCreated: () => void;
  onUnavailable?: () => void;
  onClose?: () => void;
};

const FiatBuyWidgetModal = ({
  orderId,
  cryptoAsset,
  fiatAsset,
  fiatAmount,
  onOrderCreated,
  onUnavailable,
  onClose,
}: FiatBuyWidgetModalProps) => {
  const [loading, setLoading] = useState(true);
  const [widgetUrl, setWidgetUrl] = useState<string>();
  const providerName = useBuyProvider();
  const { address } = useReceiveAddress({ assetName: cryptoAsset.name });

  const handleUnavailable = useCallback(() => {
    onClose?.();
    onUnavailable?.();
  }, [onClose, onUnavailable]);

  useEffect(() => {
    (async () => {
      if (!address || widgetUrl) {
        return;
      }

      setLoading(true);

      const url = await exodus.fiatRamp.getWidgetUrl({
        provider: PROVIDERS[providerName],
        orderId,
        paymentMethod: PAYMENT_METHOD_TYPES[DEFAULT_PAYMENT_METHOD],
        walletAddress: address,
        fromAsset: fiatAsset.name,
        fromAmount: fiatAmount,
        toAsset: [cryptoAsset.name],
        type: ORDER_TYPES.buy,
        themeId: providerName === 'moonpay' ? 'light' : undefined,
        finalUrl: window.location.origin,
      });

      setWidgetUrl(url);
      setLoading(false);
    })();
  }, [
    address,
    cryptoAsset,
    fiatAmount,
    fiatAsset,
    orderId,
    providerName,
    widgetUrl,
  ]);

  if (!loading && !widgetUrl) {
    handleUnavailable();
    return null;
  }

  return (
    <div className="flex h-full flex-col pb-8">
      {loading && (
        <LoadingSpinner className="mx-auto my-20 text-black theme-dark:text-white" />
      )}

      {widgetUrl && providerName === 'blockchain' && (
        <BlockchainWidget
          src={widgetUrl}
          onPaymentProcessed={() => {
            onClose?.();

            onOrderCreated();
          }}
        />
      )}
      {widgetUrl && providerName === 'moonpay' && (
        <MoonPayWidget
          baseCurrencyAmount={`${fiatAmount}`}
          baseCurrencyCode={fiatAsset.name}
          externalTransactionId={orderId}
          showOnlyCurrencies={cryptoAsset.displayTicker}
          src={widgetUrl}
          walletAddress={address}
          onTransactionCreated={async () => {
            onClose?.();

            onOrderCreated();
          }}
        />
      )}
    </div>
  );
};

const showBuyWidgetModal = async ({
  orderId,
  cryptoAsset,
  fiatAsset,
  fiatAmount,
  onOrderCreated,
  onUnavailable: onProviderUnavailable,
}: Omit<FiatBuyWidgetModalProps, 'onClose'>) =>
  actions.modal.show({
    title: 'Buy',
    fullscreen: true,
    reactContent: (
      <FiatBuyWidgetModal
        orderId={orderId}
        cryptoAsset={cryptoAsset}
        fiatAsset={fiatAsset}
        fiatAmount={fiatAmount}
        onOrderCreated={onOrderCreated}
        onUnavailable={onProviderUnavailable}
        onClose={() => {}} // FIXME: Improve `reactContent` type to avoid having to pass `onClose` here.
      />
    ),
  });

export default showBuyWidgetModal;
